import React, { useState, useRef } from 'react';

interface Props {
  value: string;
  onChange: (address: string) => void;
  onCoordinates?: (lat: number, lng: number) => void;
  placeholder?: string;
  required?: boolean;
  inputStyle?: React.CSSProperties;
}

async function reverseGeocode(lat: number, lng: number): Promise<{ address: string; formatted: string }> {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=16`,
    { headers: { 'User-Agent': 'Sahayy/1.0.0' } },
  );
  if (!res.ok) throw new Error('Reverse geocode failed');
  const data = await res.json();
  const a = data.address || {};
  const parts = [
    a.road ? `${a.house_number ? a.house_number + ' ' : ''}${a.road}` : null,
    a.suburb || a.neighbourhood || a.quarter || null,
    a.city || a.town || a.village || a.county || null,
    a.country || null,
  ].filter(Boolean) as string[];
  return { address: parts.join(', '), formatted: data.display_name || parts.join(', ') };
}

async function forwardGeocode(query: string): Promise<{ address: string; lat: number; lng: number } | null> {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=1`,
    { headers: { 'User-Agent': 'Sahayy/1.0.0' } },
  );
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.length) return null;
  const item = data[0];
  const a = item.address || {};
  const parts = [
    a.road ? `${a.house_number ? a.house_number + ' ' : ''}${a.road}` : (a.amenity || a.building || null),
    a.suburb || a.neighbourhood || null,
    a.city || a.town || a.village || a.county || null,
    a.country || null,
  ].filter(Boolean) as string[];
  const address = parts.length >= 2 ? parts.join(', ') : item.display_name.split(',').slice(0, 3).join(',').trim();
  return { address, lat: parseFloat(item.lat), lng: parseFloat(item.lon) };
}

export default function LocationInput({
  value,
  onChange,
  onCoordinates,
  placeholder = 'Enter location (e.g. Hyde Park, London)',
  required = false,
  inputStyle = {},
}: Props) {
  const [locating, setLocating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<{ address: string; lat: number; lng: number } | null>(null);
  const lastValidated = useRef('');

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) { setError('Geolocation not supported.'); return; }
    setLocating(true);
    setError(null);
    setSuggestion(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const { address } = await reverseGeocode(latitude, longitude);
          onChange(address);
          onCoordinates?.(latitude, longitude);
          lastValidated.current = address;
        } catch {
          setError('Failed to get address. Please enter manually.');
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        setError(err.code === err.PERMISSION_DENIED
          ? 'Location permission denied.'
          : 'Could not get location.');
      },
      { timeout: 10000 },
    );
  };

  const handleBlur = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === lastValidated.current) return;
    setValidating(true);
    setError(null);
    setSuggestion(null);
    try {
      const result = await forwardGeocode(trimmed);
      if (!result) {
        setError('Address not found. Please check the spelling or be more specific.');
      } else {
        onCoordinates?.(result.lat, result.lng);
        lastValidated.current = trimmed;
        // Offer suggestion only if the formatted address meaningfully differs
        if (result.address.toLowerCase() !== trimmed.toLowerCase()) {
          setSuggestion(result);
        }
      }
    } catch {
      // silent — don't block the user
    } finally {
      setValidating(false);
    }
  };

  const acceptSuggestion = () => {
    if (!suggestion) return;
    onChange(suggestion.address);
    onCoordinates?.(suggestion.lat, suggestion.lng);
    lastValidated.current = suggestion.address;
    setSuggestion(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#4a5568' }}>
          Location{required && <span style={{ color: '#ef4444' }}> *</span>}
        </span>
        <button
          type="button"
          onClick={handleCurrentLocation}
          disabled={locating}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none',
            backgroundColor: '#1a1f36', color: '#fff',
            fontSize: '0.78rem', fontWeight: 600, cursor: locating ? 'default' : 'pointer',
            opacity: locating ? 0.7 : 1, transition: 'background 0.15s',
          }}
          onMouseEnter={e => { if (!locating) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FF385C'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1a1f36'; }}
        >
          {locating ? (
            <>
              <div style={{ width: '10px', height: '10px', border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Locating...
            </>
          ) : (
            <>📍 Use Current</>
          )}
        </button>
      </div>

      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={value}
          onChange={e => { onChange(e.target.value); setSuggestion(null); }}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={inputStyle}
          onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
        />
        {validating && (
          <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
            <div style={{ width: '14px', height: '14px', border: '2px solid #e2e6f0', borderTop: '2px solid #FF385C', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          </div>
        )}
      </div>

      {suggestion && (
        <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.75rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', fontSize: '0.8rem' }}>
          <span style={{ color: '#0369a1' }}>📍 Verified: <strong>{suggestion.address}</strong></span>
          <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={acceptSuggestion} style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: '#0369a1', color: '#fff', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
              Use verified
            </button>
            <button type="button" onClick={() => setSuggestion(null)} style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid #bae6fd', backgroundColor: 'transparent', color: '#0369a1', fontSize: '0.75rem', cursor: 'pointer' }}>
              Keep mine
            </button>
          </div>
        </div>
      )}

      {error && (
        <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>{error}</p>
      )}
    </div>
  );
}
